import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowRight, Sparkles } from "lucide-react";

const Footer = () => {
  const quickLinks = [
    { name: "Home", path: "/" },
    { name: "About Us", path: "/about" },
    { name: "Services", path: "/services" },
    { name: "Careers", path: "/careers" },
    { name: "Contact", path: "/contact" },
  ];

  const services = [
    "Outbound Services",
    "Inbound Services",
    "Back Office Support",
    "Quality Assurance",
    "Training Programs",
  ];

  return (
    <footer data-testid="footer" className="bg-[#353537] relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute w-96 h-96 rounded-full opacity-5"
          style={{
            background: 'radial-gradient(circle, #6DB432 0%, transparent 70%)',
            top: '-10%',
            right: '-5%',
            animation: 'float 10s ease-in-out infinite',
          }}
        />
        <div 
          className="absolute w-64 h-64 rounded-full opacity-5"
          style={{
            background: 'radial-gradient(circle, #8BC064 0%, transparent 70%)',
            bottom: '10%',
            left: '-5%',
            animation: 'float 12s ease-in-out infinite reverse',
          }}
        />
      </div>

      {/* Top accent line with animation */}
      <div className="h-1 bg-gradient-to-r from-transparent via-[#6DB432] to-transparent relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
          style={{ animation: 'shine 3s ease-in-out infinite' }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="space-y-6 group">
            <Link to="/" className="inline-block transform transition-transform duration-300 hover:scale-105">
              <img
                src="/images/logo-dark.png"
                alt="NUVEXA - Powered by PRECISION"
                className="h-16 w-auto"
              />
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed">
              Delivering reliable BPO and call center solutions designed to help
              businesses grow efficiently with precision and excellence.
            </p>
            <div className="flex items-center gap-2 text-[#6DB432]">
              <Sparkles className="w-4 h-4 animate-pulse" />
              <span className="text-sm font-medium">Excellence in Every Call</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-bold text-lg mb-6 uppercase tracking-wider flex items-center gap-2 gradient-text-animate">
              <span className="w-2 h-2 rounded-full bg-[#6DB432] animate-pulse" />
              Quick Links
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={link.path} style={{ animationDelay: `${index * 0.1}s` }}>
                  <Link
                    to={link.path}
                    data-testid={`footer-${link.name.toLowerCase().replace(" ", "-")}`}
                    className="text-gray-400 hover:text-[#6DB432] transition-all duration-300 flex items-center gap-2 group hover:translate-x-2 magnetic-hover"
                  >
                    <ArrowRight className="w-4 h-4 opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-white font-bold text-lg mb-6 uppercase tracking-wider flex items-center gap-2 gradient-text-animate">
              <span className="w-2 h-2 rounded-full bg-[#6DB432] animate-pulse" />
              Our Services
            </h3>
            <ul className="space-y-3">
              {services.map((service, index) => (
                <li key={service} className="group jelly-hover" style={{ animationDelay: `${index * 0.1}s` }}>
                  <span className="text-gray-400 flex items-center gap-2 transition-all duration-300 group-hover:text-[#6DB432] group-hover:translate-x-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#6DB432] transition-transform duration-300 group-hover:scale-150" />
                    {service}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-bold text-lg mb-6 uppercase tracking-wider flex items-center gap-2 gradient-text-animate">
              <span className="w-2 h-2 rounded-full bg-[#6DB432] animate-pulse" />
              Contact Us
            </h3>
            <ul className="space-y-4">
              <li>
                <a
                  href="tel:+923356883921"
                  data-testid="footer-phone"
                  className="flex items-start gap-3 text-gray-400 hover:text-[#6DB432] transition-all duration-300 group shake-hover"
                >
                  <Phone className="w-5 h-5 text-[#6DB432] flex-shrink-0 mt-0.5 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12" />
                  <span>+92 335 688 3921</span>
                </a>
              </li>
              <li>
                <a
                  href="mailto:info@nuvexallc.com"
                  data-testid="footer-email"
                  className="flex items-start gap-3 text-gray-400 hover:text-[#6DB432] transition-all duration-300 group"
                >
                  <Mail className="w-5 h-5 text-[#6DB432] flex-shrink-0 mt-0.5 transition-transform duration-300 group-hover:scale-110" />
                  <span>info@nuvexallc.com</span>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-gray-400 group">
                  <MapPin className="w-5 h-5 text-[#6DB432] flex-shrink-0 mt-0.5 transition-transform duration-300 group-hover:scale-110 animate-bounce" style={{ animationDuration: '2s' }} />
                  <span>Office No. 1, 1st Floor, Ahmed Plaza, Range Rd, Rawalpindi, 46000</span>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm text-center md:text-left">
              &copy; {new Date().getFullYear()} NUVEXA LLC. All rights reserved.
            </p>
            <p className="text-[#6DB432] font-bold text-sm tracking-widest uppercase gradient-text-animated">
              Powered by PRECISION
            </p>
          </div>
          <div className="mt-4 text-center">
            <p className="text-gray-500 text-sm">
              Designed & Developed by{" "}
              <a
                href="https://mightyroutes.com/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mighty-routes-link"
                className="text-[#6DB432] hover:text-[#8BC064] transition-colors duration-300 font-medium hover:underline"
              >
                Mighty Routes
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
